import { Table } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import { type CSSProperties, useMemo } from 'react';

import { useMRT_ColumnVirtualizer } from '../../hooks/useMRT_ColumnVirtualizer';
import { useMRTCompatibleTheme } from '../../lib/mrt-theme';
import { useMRTColorScheme } from '../../lib/useColorScheme';
import { type MRT_RowData, type MRT_TableInstance } from '../../types';
import { resolveThemeStyle } from '../../utils/mrt-style';
import { adjustStripeHoverColor, parseCSSVarId } from '../../utils/style.utils';
import { parseFromValuesOrFunc } from '../../utils/utils';
import { Memo_MRT_TableBody, MRT_TableBody } from '../body/MRT_TableBody';
import { MRT_TableFooter } from '../footer/MRT_TableFooter';
import { MRT_TableHead } from '../head/MRT_TableHead';
import classes from './MRT_Table.module.css';

interface Props<TData extends MRT_RowData> extends React.ComponentPropsWithoutRef<'table'> {
    table: MRT_TableInstance<TData>;
}

export const MRT_Table = <TData extends MRT_RowData>({ table, ...rest }: Props<TData>) => {
    const {
        getFlatHeaders,
        getState,
        options: { columns, enableTableFooter, enableTableHead, layoutMode, mantineTableProps, memoMode },
    } = table;
    const { columnSizing, columnResizing, columnVisibility } = getState();

    const tableProps = {
        ...parseFromValuesOrFunc(mantineTableProps, { table }),
        ...rest,
    };

    const columnSizeVars = useMemo(() => {
        const headers = getFlatHeaders();
        const colSizes: { [key: string]: number } = {};
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i];
            const colSize = header.getSize();
            colSizes[`--header-${parseCSSVarId(header.id)}-size`] = colSize;
            colSizes[`--col-${parseCSSVarId(header.column.id)}-size`] = colSize;
        }
        return colSizes;
    }, [columns, columnSizing, columnResizing, columnVisibility]);

    const columnVirtualizer = useMRT_ColumnVirtualizer(table);

    const commonTableGroupProps = {
        columnVirtualizer,
        table,
    };

    const colorScheme = useMRTColorScheme();
    const theme = useMRTCompatibleTheme();

    const { __vars, className, striped, stripedColor, style, ...restTableProps } = tableProps as {
        __vars?: Record<string, number | string | undefined>;
        striped?: boolean | string;
        stripedColor?: string;
    } & React.ComponentPropsWithoutRef<'table'>;

    const stripeHover = adjustStripeHoverColor(stripedColor, colorScheme);

    const mergedStyle = {
        ...columnSizeVars,
        '--mrt-striped-row-background-color': stripedColor,
        '--mrt-striped-row-hover-background-color': stripeHover,
        ...(__vars as object),
        ...resolveThemeStyle(style as any, theme),
    } as CSSProperties;

    return (
        <Table
            className={cn('mrt-table', classes.root, layoutMode?.startsWith('grid') && classes['root-grid'], className)}
            style={mergedStyle}
            {...restTableProps}
        >
            {enableTableHead && <MRT_TableHead {...commonTableGroupProps} />}
            {memoMode === 'table-body' || columnResizing.isResizingColumn ? (
                <Memo_MRT_TableBody {...commonTableGroupProps} tableProps={tableProps} />
            ) : (
                <MRT_TableBody {...commonTableGroupProps} tableProps={tableProps} />
            )}
            {enableTableFooter && <MRT_TableFooter {...commonTableGroupProps} />}
        </Table>
    );
};
